import React, { useState } from 'react';
import {
  ArrowPathIcon,
  EnvelopeIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import AuthLayout, { AuthLabel, AuthError, AuthLink, SocialLoginButtons } from '../components/ui/AuthLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PasswordInput, { generateSecurePassword, passwordMeetsRules } from '../components/ui/PasswordInput';

// ── Email Sent screen ─────────────────────────────────────────────────────────
const EmailSentScreen = ({ email, onResend }) => {
  const { t } = useLanguage();
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);

  const handleResend = async () => {
    setResending(true);
    await onResend(email);
    setResending(false);
    setResent(true);
    setTimeout(() => setResent(false), 4000);
  };

  return (
    <AuthLayout
      title="Konfirmo email-in"
      subtitle="Llogaria u krijua! Shko te email-i yt dhe kliko linkun."
    >
      <div className="space-y-4">
        {/* Icon */}
        <div className="flex justify-center">
          <div className="w-14 h-14 rounded-2xl bg-zinc-950 flex items-center justify-center shadow-sm">
            <EnvelopeIcon className="w-7 h-7 text-yellow-400" />
          </div>
        </div>

        {/* Email display */}
        <div className="rounded-xl bg-zinc-50 border border-zinc-200/60 px-4 py-3 text-center space-y-0.5">
          <p className="text-[11px] text-zinc-500 font-medium">{t('Kemi dërguar linkun te:')}</p>
          <p className="text-xs font-bold text-zinc-900 break-all">{email}</p>
        </div>

        {/* Steps */}
        <ol className="space-y-2 text-xs text-zinc-600">
          {[
            'Hap aplikacionin e email-it',
            'Gjej email-in nga DeutschLernen',
            'Kliko butonin "Confirm your email"',
            'Kthehu dhe hyr në platformë',
          ].map((step, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span className="mt-0.5 w-5 h-5 rounded-full bg-zinc-950 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                {i + 1}
              </span>
              <span>{t(step)}</span>
            </li>
          ))}
        </ol>

        {/* Resend + back */}
        <div className="space-y-2 pt-2">
          {resent ? (
            <div className="flex items-center justify-center gap-1 text-xs text-green-700 font-semibold">
              <CheckCircleIcon className="w-3.5 h-3.5" /> {t('Email u ridërgua!')}
            </div>
          ) : (
            <Button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="w-full h-9 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 text-xs font-semibold rounded-lg"
              variant="outline"
            >
              {resending && <ArrowPathIcon className="w-3.5 h-3.5 animate-spin mr-1.5" />}
              {t('Ridërgo email-in')}
            </Button>
          )}
          <p className="text-center text-xs">
            <AuthLink to="/login" className="text-zinc-500 hover:text-zinc-900">
              ← {t('Kthehu te hyrja')}
            </AuthLink>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

// ── Register form ─────────────────────────────────────────────────────────────
const RegisterPage = () => {
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', profession: '', password: '', confirmPassword: '',
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [toast, setToast] = useState(null);

  const { register, loginWithProvider, resendConfirmationEmail, loading, error } = useAuth();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setFieldErrors((current) => ({ ...current, [e.target.name]: '' }));
  };

  const setPasswordFields = (fields) => {
    setFormData((current) => ({ ...current, ...fields }));
    setFieldErrors((current) => ({ ...current, password: '', confirmPassword: '' }));
  };

  const passwordsMatch = !formData.confirmPassword || formData.password === formData.confirmPassword;

  const nextStep = (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!formData.firstName.trim()) nextErrors.firstName = 'Shkruaj emrin.';
    if (!formData.lastName.trim()) nextErrors.lastName = 'Shkruaj mbiemrin.';
    if (!formData.email.trim()) nextErrors.email = 'Shkruaj email-in.';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) nextErrors.email = 'Email-i nuk është valid.';
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordsMatch || !passwordMeetsRules(formData.password)) {
      setFieldErrors({ confirmPassword: !passwordsMatch ? 'Fjalëkalimet nuk përputhen.' : '', password: !passwordMeetsRules(formData.password) ? 'Fjalëkalimi nuk i plotëson kërkesat.' : '' });
      return;
    }
    const username = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
    const result = await register(username, formData.email, formData.password, {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      profession: formData.profession.trim(),
    });
    if (result.success && result.needsConfirmation) {
      setRegisteredEmail(result.email);
      setToast({ type: 'success', message: t('Llogaria u krijua. Kontrollo email-in.') });
    } else if (!result.success) {
      setToast({ type: 'error', message: result.error });
    }
  };

  if (registeredEmail) {
    return (
      <EmailSentScreen
        email={registeredEmail}
        onResend={resendConfirmationEmail}
      />
    );
  }

  return (
    <AuthLayout
      title="Krijo llogarinë"
      subtitle="Fillo falas dhe ndiq progresin tënd drejt B2."
      footer={
        <>
          {t('Keni llogari?')}{' '}
          <AuthLink to="/login">{t('Hyr këtu')}</AuthLink>
        </>
      }
    >
      <form className="space-y-4" onSubmit={step === 1 ? nextStep : handleSubmit}>
        {error && <AuthError>{t(error)}</AuthError>}

        <div className="flex items-center gap-2">
          {[1, 2].map((item) => <span key={item} className={`h-1.5 flex-1 rounded-full ${item <= step ? 'bg-[#FFCE00]' : 'bg-zinc-200'}`} />)}
        </div>

        {step === 1 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <AuthLabel htmlFor="firstName">{t('Emri')}</AuthLabel>
                <Input id="firstName" name="firstName" required value={formData.firstName} onChange={handleChange} placeholder="Filan" className={`h-11 rounded-xl bg-white placeholder:text-zinc-400 text-sm ${fieldErrors.firstName ? 'border-red-500' : 'border-zinc-200'} focus:border-[#DD0000] focus:bg-white`} />
                {fieldErrors.firstName && <p className="text-xs text-red-600">{t(fieldErrors.firstName)}</p>}
              </div>
              <div className="space-y-1">
                <AuthLabel htmlFor="lastName">{t('Mbiemri')}</AuthLabel>
                <Input id="lastName" name="lastName" required value={formData.lastName} onChange={handleChange} placeholder="Fisteku" className={`h-11 rounded-xl bg-white placeholder:text-zinc-400 text-sm ${fieldErrors.lastName ? 'border-red-500' : 'border-zinc-200'} focus:border-[#DD0000] focus:bg-white`} />
                {fieldErrors.lastName && <p className="text-xs text-red-600">{t(fieldErrors.lastName)}</p>}
              </div>
            </div>
            <div className="space-y-1">
              <AuthLabel htmlFor="email">Email</AuthLabel>
              <Input id="email" name="email" type="email" autoComplete="email" required value={formData.email} onChange={handleChange} placeholder="filan.fisteku@email.com" className={`h-11 rounded-xl bg-white placeholder:text-zinc-400 text-sm ${fieldErrors.email ? 'border-red-500' : 'border-zinc-200'} focus:border-[#DD0000] focus:bg-white`} />
              {fieldErrors.email && <p className="text-xs text-red-600">{t(fieldErrors.email)}</p>}
            </div>
            <div className="space-y-1">
              <AuthLabel htmlFor="profession">{t('Profesioni')} <span className="normal-case font-normal text-zinc-400">{t('(opsionale)')}</span></AuthLabel>
              <Input id="profession" name="profession" value={formData.profession} onChange={handleChange} placeholder={t('p.sh. Mësues, infermier, programues...')} className="h-11 rounded-xl border-zinc-200 bg-white placeholder:text-zinc-400 text-sm" />
            </div>
          </div>
        ) : (
        <>
        <div className="grid grid-cols-1 gap-3">
          {/* Password */}
          <div className="space-y-1">
            <AuthLabel htmlFor="password">{t('Fjalëkalimi')}</AuthLabel>
            <PasswordInput
              id="password"
              name="password"
              required
              value={formData.password}
              onChange={(value) => setPasswordFields({ password: value })}
              onGenerate={() => {
                const generated = generateSecurePassword();
                setPasswordFields({ password: generated, confirmPassword: generated });
                setShowConfirm(true);
              }}
              showStrength
              placeholder={t('Fjalëkalimi...')}
              autoComplete="new-password"
            />
            {fieldErrors.password && <p className="text-xs text-red-600">{t(fieldErrors.password)}</p>}
          </div>

          {/* Confirm Password */}
          <div className="space-y-1">
            <AuthLabel htmlFor="confirmPassword">{t('Konfirmo')}</AuthLabel>
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              required
              value={formData.confirmPassword}
              onChange={(value) => setPasswordFields({ confirmPassword: value })}
              visible={showConfirm}
              onVisibleChange={setShowConfirm}
              invalid={!passwordsMatch}
              placeholder={t('Konfirmo...')}
              autoComplete="new-password"
            />
          </div>
        </div>
        {!passwordsMatch && (
          <p className="text-xs font-medium text-red-600">{t('Fjalëkalimet nuk përputhen')}</p>
        )}
        {fieldErrors.confirmPassword && <p className="text-xs text-red-600">{t(fieldErrors.confirmPassword)}</p>}

        {/* Submit */}
        <Button
          type="submit"
          disabled={loading || !passwordsMatch}
          className="w-full h-11 bg-black hover:bg-zinc-800 text-white font-bold rounded-xl text-sm transition-colors mt-2"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-1.5">
              <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
              {t('Duke krijuar...')}
            </span>
          ) : (
            t('Krijo llogari')
          )}
        </Button>
        <button type="button" onClick={() => setStep(1)} className="w-full text-xs font-semibold text-zinc-500 hover:text-zinc-950">{t('Kthehu te të dhënat')}</button>
        <div className="relative py-1">
          <div className="border-t border-zinc-200" />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xs px-3 text-[11px] text-zinc-400">{t('ose vazhdo me')}</span>
        </div>
        <SocialLoginButtons onProvider={loginWithProvider} loading={loading} />

        <p className="text-center text-[10px] text-zinc-450 leading-relaxed pt-1">
          Duke u regjistruar, pranoni{' '}
          <a href="#terms" className="font-semibold text-zinc-550 underline underline-offset-4 hover:text-yellow-600 transition-colors hover:text-zinc-900">Kushtet</a>
          {' '}{t('dhe')}{' '}
          <a href="#privacy" className="font-semibold text-zinc-550 underline underline-offset-4 hover:text-yellow-600 transition-colors hover:text-zinc-900">{t('Privatësinë')}</a>.
        </p>
        </>
        )}
        {step === 1 && <Button type="submit" className="h-11 w-full rounded-xl bg-black text-sm font-bold text-white hover:bg-zinc-800">{t('Vazhdo')}</Button>}
        {toast && <div role="status" className={`fixed bottom-5 right-5 z-50 rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-xl ${toast.type === 'success' ? 'bg-emerald-600' : 'bg-[#DD0000]'}`}>{t(toast.message)}</div>}
      </form>
    </AuthLayout>
  );
};

export default RegisterPage;
